import React, { memo } from 'react';
import { Tag, Tooltip } from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    HourglassOutlined,
    ShoppingOutlined,
    SyncOutlined,
    EditOutlined,
    EyeOutlined,
    StarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { TrelloIcon, GoogleDriveIcon } from './icons';

// Memoized Order Status Component
export const MemoizedOrderStatus = memo(({ status }) => {
    let color = '';
    let icon = null;

    switch (status) {
        case 'Shipped':
            color = 'green';
            icon = <CheckCircleOutlined />;
            break;
        case 'PartiallyShipped':
            color = 'processing';
            icon = <SyncOutlined spin />;
            break;
        case 'Unshipped':
            color = 'orange';
            icon = <ClockCircleOutlined />;
            break;
        case 'Canceled':
            color = 'error';
            icon = <DeleteOutlined />;
            break;
        case 'Pending':
            icon = <HourglassOutlined />;
            break;
        default:
            color = 'default';
            icon = <ShoppingOutlined />;
    }

    return <span style={{ width: 100, textAlign: 'center' }} icon={icon} color={color}>{status}</span>;
});

// Memoized Customization Status Component
export const MemoizedCustomizationStatus = memo(({ status, trelloUrl, customizationStorageUrl, mockupSentAt }) => {
    let color = '';
    let icon = null;
    
    // Check if MockupSent status is overdue (> 12 hours)
    const isMockupSentOverdue = status === 'MockupSent' && mockupSentAt && 
        dayjs().diff(dayjs(mockupSentAt), 'hour') > 12;
    
    switch (status) {
        case 'MockupSent':
            color = 'green';
            icon = <CheckCircleOutlined />;
            break;
        case 'Rework':
            color = 'orange';
            icon = <EditOutlined />;
            break;
        case 'Confirming':
            color = 'processing';
            icon = <ClockCircleOutlined />;
            break;
        case 'AutoFulfill':
            color = 'purple';
            icon = <CheckCircleOutlined />;
            break;
        case 'CustomerConfirmed':
            color = 'green';
            icon = <CheckCircleOutlined />;
            break;
        case 'ConfirmedNoReply':
            color = 'cyan';
            icon = <CheckCircleOutlined />;
            break;
        case 'TaskCreated':
            color = 'gold';
            icon = <StarOutlined />;
            break;
        case 'Doing':
            color = 'blue';
            icon = <EditOutlined />;
            break;
        case 'InternalReview':
            color = 'magenta';
            icon = <EyeOutlined />;
            break;
        case 'MockupReady':
            color = 'blue';
            icon = <CheckCircleOutlined />;
            break;
        default:
            color = 'default';
    }

    return (
        <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            width: '200px'
        }}>
            <Tag 
                style={{ 
                    flex: 1,
                    textAlign: 'center',
                    margin: 0
                }} 
                icon={icon} 
                color={color}
            >
                {status || '-'}
            </Tag>
            {customizationStorageUrl && (
                <Tooltip title="Open in Google Drive">
                    <div 
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '16px'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            window.open(customizationStorageUrl, '_blank');
                        }}
                    >
                        <GoogleDriveIcon />
                    </div>
                </Tooltip>
            )}
            {trelloUrl && (
                <Tooltip title="Open in Trello">
                    <div 
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '16px'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            window.open(trelloUrl, '_blank');
                        }}
                    >
                        <TrelloIcon />
                    </div>
                </Tooltip>
            )}
            {isMockupSentOverdue && (
                <Tooltip title={`Khách hàng chưa reply sau ${dayjs().diff(dayjs(mockupSentAt), 'hour')} giờ`}>
                    <ClockCircleOutlined
                        style={{ 
                            color: '#ff7875', 
                            fontSize: 14,
                            cursor: 'help'
                        }}
                    />
                </Tooltip>
            )}
        </div>
    );
});

// Set display names for better debugging
MemoizedOrderStatus.displayName = 'MemoizedOrderStatus';
MemoizedCustomizationStatus.displayName = 'MemoizedCustomizationStatus';